import { useCallback, useContext, useEffect, useState, lazy, Suspense } from 'react'
import { UIView, useRouter, useCurrentStateAndParams } from '@uirouter/react'
import AuthContext from '../../authService/Context'
import LoadingUser from './LoadingUser'
import { Alert, Button, Space } from 'antd'
import authService from '../../authService'
import { getFirstLoginAccess } from '../../authService/firstLoginAccess'
import FirstLoginSessionNotice from './FirstLoginSessionNotice'
import { isPasswordRecoveryState } from '../../authService/passwordRecoveryLink'
import { isCollaboratorInvitationState } from '../../authService/collaboratorInvitationLink'

const RolesNotFound = lazy(() => import('./RolesNotFound'))
const StatusNotActive = lazy(() => import('./StatusNotActive'))
const LoginPage = lazy(() => import('./LoginPage'))
const ForgotPasswordPage = lazy(() => import('./ForgotPasswordPage'))
const RegisterPage = lazy(() => import('./RegisterPage'))
const Layout = lazy(() => import('../../layout'))
const FirstLogin = lazy(() => import('../FirstLogin'))
const CollaboratorInvitation = lazy(() => import('../CollaboratorInvitation'))

export default function Init() {
  const { user, getting, profileError, claimRecovery } = useContext(AuthContext)
  const router = useRouter()
  const { state } = useCurrentStateAndParams()
  const { roles, isAuthenticated, status } = user
  const currentStateName = state?.name
  const { stateService } = router
  const access = getFirstLoginAccess(user)
  const [wizardTenant, setWizardTenant] = useState(null)
  const onAlreadyCompleted = useCallback(() => setWizardTenant(null), [])
  const recoveryBlocked = ['refreshing', 'reauthenticate', 'failed'].includes(claimRecovery?.status)
  const showWizard = !recoveryBlocked && access.canConfigure && access.tokenReady && status === 'ACTIVE' && (access.needsWizard || wizardTenant === access.tenantId)

  useEffect(() => {
    if (access.needsWizard) setWizardTenant(access.tenantId)
    else if (!isAuthenticated || !access.canConfigure || wizardTenant !== access.tenantId) setWizardTenant(null)
  }, [access.needsWizard, access.tenantId, access.canConfigure, isAuthenticated, wizardTenant])

  useEffect(() => {
    if (isPasswordRecoveryState(currentStateName) || isCollaboratorInvitationState(currentStateName)) return
    if (!isAuthenticated) {
      if (!currentStateName || currentStateName === 'firstLogin' || currentStateName === 'App' || currentStateName.startsWith('App.')) {
        stateService.go('App')
      }

      return
    }

    if (getting || !user.profileLoaded || profileError || (status && status !== 'ACTIVE') || access.blockedReason || recoveryBlocked) return
    if (showWizard) {
      if (currentStateName !== 'firstLogin') stateService.go('firstLogin', {}, { location: 'replace' })
    } else if (['App', 'forgotPassword', 'register', 'firstLogin'].includes(currentStateName)) {
      stateService.go('App.Dashboard', {}, { location: 'replace' })
    }
  }, [currentStateName, isAuthenticated, stateService, getting, user.profileLoaded, profileError, status, showWizard, access.blockedReason, recoveryBlocked])

  if (isPasswordRecoveryState(currentStateName)) {
    return <Suspense fallback={<LoadingUser />}><ForgotPasswordPage /></Suspense>
  }

  if (isCollaboratorInvitationState(currentStateName)) {
    return <Suspense fallback={<LoadingUser />}><CollaboratorInvitation /></Suspense>
  }

  if (isAuthenticated && (recoveryBlocked || access.blockedReason)) {
    return <FirstLoginSessionNotice reason={access.blockedReason} recovery={claimRecovery} />
  }

  if (getting) return <LoadingUser />

  if (isAuthenticated && profileError) {
    return <div style={{ maxWidth: 600, margin: '15vh auto', padding: 24 }}>
      <Alert type="warning" showIcon message={profileError} />
      <Space style={{ marginTop: 20 }}><Button onClick={() => authService.retryProfile()}>Reintentar</Button><Button onClick={() => authService.logout()}>Volver al login</Button></Space>
    </div>
  }

  if (isAuthenticated && !user.profileLoaded) return <LoadingUser />

  if (isAuthenticated && !access.canConfigure && (!roles || (Array.isArray(roles) && roles.length === 0))) {
    return (
      <Suspense fallback={<LoadingUser />}>
        <RolesNotFound />
      </Suspense>
    )
  }

  if (isAuthenticated && status && status !== 'ACTIVE'){
    return (
      <Suspense fallback={<LoadingUser/>}>
        <StatusNotActive/>
      </Suspense>
    )
  }

  if (isAuthenticated && showWizard) {
    return <Suspense fallback={<LoadingUser />}><FirstLogin key={access.tenantId} tenantId={access.tenantId} onAlreadyCompleted={onAlreadyCompleted} /></Suspense>
  }

  let unauthenticatedView = <LoginPage />

  if (currentStateName === 'register') {
    unauthenticatedView = <RegisterPage />
  }

  return (
    <Suspense fallback={<LoadingUser />}>
      {isAuthenticated ? (
        <Layout>
          <UIView name="main" />
        </Layout>
      ) : (
        unauthenticatedView
      )}
    </Suspense>
  )
}
